import React from "react";
import PropTypes from "prop-types";
import TableCell from './table_cell';
import { resolveColumnTitle } from '../utils/column_utils';
import { VIEW_MODES } from '../constants';
import { componentText } from '../utils/i18n_utils';

const t = (key, fallback) => componentText(`header_row.${key}`, fallback);

// A column title can carry a league-set override (free text). Render it as
// React children so every tag is escaped by React; only <br> is honored, as a
// real line break — mirroring the HTML leaderboard's String#semi_safe. Never
// feed these titles to dangerouslySetInnerHTML.
const renderColumnTitle = (title) =>
  String(title ?? '')
    .split(/<br\s*\/?>/i)
    .flatMap((segment, index) => (index === 0 ? [segment] : [<br key={index} />, segment]));

// A sortable column header. `data-sort-dir` is set only on the active column,
// so a header never advertises a direction that isn't its own.
const SortButton = ({ columnKey, sortKey, sortDir, onSort, children }) => {
  const isActive = sortKey === columnKey;
  return (
    <button
      type="button"
      className={`lbv2__sort-btn ${isActive ? 'is-sorted' : ''}`}
      data-sort-dir={isActive ? sortDir : undefined}
      onClick={() => onSort(columnKey)}
    >
      {children}
    </button>
  );
};

SortButton.propTypes = {
  columnKey: PropTypes.string.isRequired,
  sortKey: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func.isRequired,
  children: PropTypes.node,
};

const HeaderRow = ({ data, handicapFormat, columns = [], roundToParTitle = null, roundThruTitle = null, displayedFlags, viewMode, isManagerSite, sortKey, sortDir, onSort }) => {
  const scopes = Object.values(data.scopes || {});
  if (!scopes.length) return null;

  const firstScope = scopes[0];
  const aggs = Object.values(firstScope || {});
  if (!aggs.length) return null;

  const firstAgg = aggs[0];

  const columnIds = new Set(columns.map((column) => column.id));

  const movePresent = firstAgg.posChange;

  const handicapLabel = (() => {
    switch (handicapFormat) {
      case 'usga_net': return t('net', 'Net');
      case 'gross': return t('gross', 'Gross');
      default: return '';
    }
  })();

  const pastRounds = firstAgg.pastRounds || [];
  const inProgressPastRounds = pastRounds.filter((pastRound) => data.rounds[pastRound.id].inProgress);
  const hasThruToParColumns = columnIds.has('thru') || inProgressPastRounds.length > 0;
  const totalBeforePastRounds = viewMode === VIEW_MODES.MOBILE && pastRounds.length > 0 && !hasThruToParColumns && columnIds.has('total');

  const renderPastRoundPairsHeader = () => {
    const thruLabel = t('thru', 'Thru');
    const toParLabel = t('to_par', 'To Par');
    // League-set override for the in-progress rounds' Thru headers (sparse
    // round_thru entry, sibling of round_to_par below - consumed ahead of its
    // backend producer, so absence keeps the localized default). Mobile keeps
    // the short default label. Free text - render only via
    // renderColumnTitle.
    const thruHeaderContent =
      viewMode !== VIEW_MODES.MOBILE && roundThruTitle
        ? renderColumnTitle(roundThruTitle)
        : thruLabel;
    // League-set override for the in-progress rounds' To Par columns (the
    // sparse round_to_par entry of the payload's columns array, extracted by
    // normalizeResultScope). One custom text replaces the whole client-built
    // To Par / handicap / round-name stack on every round, mirroring the HTML
    // leaderboard; mobile shows Today instead. Free text - render
    // only via renderColumnTitle.
    const toParOverride = roundToParTitle;

    const toParHeaderContent = (pastRound) => {
      if (viewMode === VIEW_MODES.MOBILE) return t('today', 'Today');
      if (toParOverride) return renderColumnTitle(toParOverride);
      return (
        <>
          {toParLabel}
          {handicapLabel && (
            <>
              <br />
              {handicapLabel}
            </>
          )}
          <br />
          {pastRound.name}
        </>
      );
    };

    return inProgressPastRounds.map((pastRound, idx) => {
      const roundId = String(pastRound.id);
      const thruKey = `past_round_${roundId}_thru`;
      const toParKey = `past_round_${roundId}_to_par`;

      return (
        <React.Fragment key={pastRound.id ?? idx}>
          <TableCell as="th" className={`lbv2__past_round_thru ${idx === 0 ? 'current-thru' : ''}`}>
            <SortButton columnKey={thruKey} sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
              <span>{thruHeaderContent}</span>
            </SortButton>
          </TableCell>
          <TableCell as="th" className="lbv2__past_round_to_par">
            <SortButton columnKey={toParKey} sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
              <span>{toParHeaderContent(pastRound)}</span>
            </SortButton>
          </TableCell>
        </React.Fragment>
      );
    });
  };

  const renderPastRoundTotalsHeader = () => pastRounds.map((pastRound, idx) => {
    const roundId = String(pastRound.id);
    const totalKey = `past_round_${roundId}_total`;

    return (
      <TableCell as="th" key={pastRound.id ?? idx} className="lbv2__past_round_total">
        <SortButton columnKey={totalKey} sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
          <span>{viewMode === VIEW_MODES.MOBILE ? `R${idx + 1}` : pastRound.name}</span>
        </SortButton>
      </TableCell>
    );
  });

  const columnTitle = (column) => {
    if (viewMode === VIEW_MODES.MOBILE && ['total', 'score'].includes(column.id)) return t('total', 'Total');
    return resolveColumnTitle(column);
  };

  const renderTotalHeader = () => {
    const totalColumn = columns.find((column) => column.id === 'total') ?? { id: 'total' };
    return (
      <TableCell as="th" className="lbv2__total">
        <SortButton columnKey="total" sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
          <span>{renderColumnTitle(columnTitle(totalColumn))}</span>
        </SortButton>
      </TableCell>
    );
  };

  return (
    <tr className="lbv2__header-row">
      {columns.map((column) => {
        const key = column.id;
        if (key === "affiliation") return null;
        if (totalBeforePastRounds && key === "total") return null;

        const isStickyCol = ['name', 'position'].includes(key) ? "sticky" : "";
        // Live-round marker for the mobile stylesheet: the collapsed table keeps
        // a third data column on screen while a Thru column is present. The
        // multi-round pair's first Thru header carries the same class.
        const liveThruMarker = key === 'thru' ? 'current-thru' : '';
        return (
          <React.Fragment key={key}>

            {key === "name" && ['country_flags', 'state_flags'].includes(displayedFlags) && (
              <TableCell as="th" className={`lbv2__flags ${displayedFlags} sticky`} />
            )}

            <TableCell as="th" className={`lbv2__${key} ${isStickyCol} ${liveThruMarker}`}>
              <SortButton columnKey={key} sortKey={sortKey} sortDir={sortDir} onSort={onSort}>
                <span>{renderColumnTitle(columnTitle(column))}</span>
              </SortButton>
            </TableCell>

            {key === "score" && (
              <>
                {totalBeforePastRounds && renderTotalHeader()}
                {renderPastRoundPairsHeader()}
                {renderPastRoundTotalsHeader()}
              </>
            )}

            {key === "position" && (
              <>
                {!isManagerSite && <TableCell as="th" className="lbv2__favorite sticky" />}
                {viewMode !== VIEW_MODES.MOBILE && movePresent !== undefined &&
                  <TableCell as="th" className="lbv2__move sticky">
                    <span className="lbv2__arrow-up-arrow-down fas fa-arrow-up-arrow-down" />
                  </TableCell>
                }
              </>
            )}
          </React.Fragment >
        );
      })}
    </tr>
  );
};

HeaderRow.propTypes = {
  data: PropTypes.object.isRequired,
  handicapFormat: PropTypes.string,
  columns: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    titleKey: PropTypes.string,
    titleText: PropTypes.string,
  })),
  roundToParTitle: PropTypes.string,
  roundThruTitle: PropTypes.string,
  displayedFlags: PropTypes.string,
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)).isRequired,
  isManagerSite: PropTypes.bool,
  sortKey: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func,
};

export default HeaderRow;
